import { useCallback, useEffect, useMemo } from 'react';

import { useInfiniteListConsumerStudentFees } from '@givebacks/api/student';
import { useNavigate } from 'react-router-dom';
import { Center, Loader, LoadingOverlay, Stack, Text } from 'ui';
import { useInViewport } from 'ui/Hooks';

import { FeeDisplayCard } from '../../../molecules/cards/feeDisplay/FeeDisplayCard';

interface StudentUnpaidFeesListProps {
  studentId: string;
}

const NoUnpaidFees = () => {
  return (
    <Center mih="200px">
      <Stack gap={24}>
        <Stack gap={8}>
          <Text size="18px" fw="600" ta="center">
            No Fees Found
          </Text>
          <Text size="14px" fw="400" ta="center">
            No student fees are currently due.
          </Text>
        </Stack>
      </Stack>
    </Center>
  );
};

const UnpaidFeesLoader = ({
  isFetchingNextPage,
  handleLoadMoreFees,
}: {
  isFetchingNextPage: boolean;
  handleLoadMoreFees: () => void;
}) => {
  const { ref, inViewport } = useInViewport();

  useEffect(() => {
    if (inViewport && !isFetchingNextPage) {
      handleLoadMoreFees();
    }
  }, [handleLoadMoreFees, inViewport, isFetchingNextPage]);

  return (
    <Center ref={ref} py="xl">
      {isFetchingNextPage && <Loader />}
    </Center>
  );
};

const NoFeesGuard = ({ studentId }: { studentId: string }) => {
  const navigate = useNavigate();
  const { data, isFetchingNextPage, fetchNextPage, hasNextPage, isError } =
    useInfiniteListConsumerStudentFees({
      studentId: studentId,
      queryOptions: {
        searches: {
          status: {
            value: 'unpaid',
          },
        },
      },
    });

  const handleLoadMoreFees = useCallback(() => {
    if (hasNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const handleFeeClick = useCallback(
    (feeId: string) => {
      navigate(`fee/${feeId}`);
    },
    [navigate]
  );

  const unpaidFeesList = useMemo(() => {
    if (!data?.pages || !data.pages?.[0]?.student_fees.length) {
      return null;
    }
    return data.pages.map((page) =>
      page.student_fees.map((studentFee) => (
        <FeeDisplayCard key={studentFee.uuid} studentFee={studentFee} onClick={handleFeeClick} />
      ))
    );
  }, [data, handleFeeClick]);

  if (isError) {
    return <NoUnpaidFees />;
  }

  if (!unpaidFeesList) {
    return <NoUnpaidFees />;
  }
  return (
    <Stack>
      {unpaidFeesList}
      <UnpaidFeesLoader
        isFetchingNextPage={isFetchingNextPage}
        handleLoadMoreFees={handleLoadMoreFees}
      />
    </Stack>
  );
};

const StudentUnpaidFeesList = ({ studentId }: StudentUnpaidFeesListProps) => {
  const { isLoading } = useInfiniteListConsumerStudentFees({
    studentId: studentId,
    queryOptions: {
      searches: {
        status: {
          value: 'unpaid',
        },
      },
    },
  });
  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  return <NoFeesGuard studentId={studentId} />;
};
export { StudentUnpaidFeesList };
